import classnames from 'classnames'
import React, { Children, forwardRef } from 'react'

import { PreviousArrow, NextArrow } from './arrows/index'
import styles from './custom-swiper.module.scss'
import { getArrowClasses } from './helper'
import { useCustomSwiper } from './hooks/useCustomSwiper'
import { useTrackEvents } from './hooks/useTrackEvents'
import { SwiperProps } from './types/custom-swiper.types'

const SuperSwiper = forwardRef<HTMLDivElement, SwiperProps>(function SuperSwiperEl(props, ref) {
  const {
    containerRef,
    activeIndex,
    isBeginning,
    isEnd,
    scrollToNext,
    scrollToPrev,
    canNavigate,
    totalSlides,
    handleScroll,
  } = useCustomSwiper(props)
  const {
    showDots,
    arrowColor,
    spaceBetween,
    dotsPosition,
    children,
    wrapperClass,
    className,
    slideClassName,
    isVertical,
    prevIconClass,
    nextIconClass,
    arrowOnClick,
  }: SwiperProps = props

  const { trackNext, trackPrev } = useTrackEvents(props)

  return (
    <>
      <div className={getArrowClasses({ ...props, canNavigate, totalSlides })}>
        <PreviousArrow
          {...props}
          id="prev-swiper-arrow"
          className={classnames(styles.prevArrow, styles.navIcon, styles.navIcon__prev, prevIconClass)}
          arrowColor={arrowColor}
          disabled={isBeginning}
          ariaLabel={props.ariaLabel}
          onClick={() => {
            scrollToPrev()
            arrowOnClick &&
              arrowOnClick({
                // @i18n-ignore
                direction: 'prev',
              })
            trackPrev()
          }}
        />
        <NextArrow
          {...props}
          id="next-swiper-arrow"
          className={classnames(styles.nextArrow, styles.navIcon, nextIconClass)}
          arrowColor={arrowColor}
          disabled={isEnd}
          ariaLabel={props.ariaLabel}
          onClick={() => {
            scrollToNext()
            arrowOnClick &&
              arrowOnClick({
                // @i18n-ignore
                direction: 'next',
              })
            trackNext()
          }}
        />
      </div>
      <div className={styles.swiperContainer} ref={containerRef}>
        <div
          className={classnames(wrapperClass, className, styles.swiperWrapper)}
          role="wrapper"
          style={{
            // overflow: 'auto',
            flexDirection: isVertical ? 'column' : 'row',
            scrollSnapType: isVertical ? 'y mandatory' : 'x mandatory',
            // @i18n-ignore
            width: 'max-content',
          }}
          onScroll={handleScroll}
          aria-label={isVertical ? 'wrapperVertical' : 'wrapperHorizontal'}
        >
          {Children.map(children, (child, index) => (
            <div
              key={index}
              className={classnames(slideClassName, styles.swiperSlide, {
                [styles.active ?? '']: index === activeIndex,
              })}
              style={{
                // @i18n-ignore
                scrollSnapAlign: 'start',
                marginRight: spaceBetween,
                marginBottom: isVertical ? '5px' : 'none',
              }}
            >
              {child}
            </div>
          ))}
        </div>
      </div>

      {showDots && (
        <div
          className={classnames(styles.pagination, { [styles[`dot_position__${dotsPosition}`] ?? '']: dotsPosition })}
        >
          {Children.map(children, (_, index) => (
            <span key={index} className={classnames(styles.dot, { [styles.active ?? '']: index === activeIndex })} />
          ))}
        </div>
      )}
    </>
  )
})

export default SuperSwiper
